import { TransactionView } from "../../../../models/view/transaction/transaction.view";
import {
  GeneratedCollection,
  GeneratedTransactionParams,
} from "../../generated.collection.abstraction";
import { BalanceAlignmentWorkerResult } from "@bitwarden/web-vault/app/services/web-worker/balance-alignment/balance-alignment.worker.result";
import { BalanceAlignmentWorkerMessage } from "@bitwarden/web-vault/app/services/web-worker/balance-alignment/balance-alignment.worker.message";
import { NormalizeTransaction } from "@bitwarden/web-vault/app/models/view/transaction/normalize/normalizeTransaction.utils";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { inject, signal, Signal, WritableSignal } from "@angular/core";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { Observable } from "rxjs";
import {
  enrichAllocations,
  getTransactionViewOf,
} from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import { toObservable } from "@angular/core/rxjs-interop";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { ReferenceDataView } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";

export class BalanceAlignmentTransactionsGeneratedCollection extends GeneratedCollection<TransactionView> {
  protected log: LogService = inject(LogService);
  protected userStoreService: UserStoreService = inject(UserStoreService);

  /** Bumped by clear(). clear() can't cancel a generateTransactions() that is already awaiting the
   * worker (e.g. logout mid-refresh), so generateTransactions() captures this before its await
   * and drops the worker's result if a clear landed meanwhile, instead of writing the previous
   * session's aligned transactions into the cleared collection. */
  private epoch = 0;
  protected generatedTransactions: TransactionView[] = [];
  protected updatedTransactions: Record<string, number> = {};

  // todo @michelle should clean that up I just add the require prop for the moment.
  protected _collection: WritableSignal<TransactionView[]> = signal<TransactionView[]>(null);
  collection: Signal<TransactionView[]> = this._collection.asReadonly();
  collection$: Observable<TransactionView[]> = toObservable(this.collection);

  update(transactions: TransactionView[]): void {
    this.generatedTransactions = transactions;
  }

  clear(): void {
    this.epoch++;
    this.generatedTransactions = [];
    this.updatedTransactions = {};
    this._collection.set(null);
  }

  async generateTransactions(
    parameters: GeneratedTransactionParams,
    referenceData: ReferenceDataView[],
  ): Promise<void> {
    const epoch = this.epoch;
    const isStale = () => epoch !== this.epoch;
    this.generatedTransactions = [];
    this.updatedTransactions = {};

    const { webWorkerQueue, transactions } = parameters;

    const balanceAlignmentRequest = new BalanceAlignmentWorkerMessage(
      "realignTransactionBalances",
      transactions,
    );

    // run the balance alignment webworker on these transactions
    await webWorkerQueue
      .postMessagePromise(balanceAlignmentRequest)
      .then((workerMessage: BalanceAlignmentWorkerResult) => {
        // Cleared mid-flight (logout), or no preferences to normalise against: drop the result.
        // A logout no longer reaches the preference check (the stores that own this collection,
        // transaction and scenario, clear it before userStore.clearStore() nulls the
        // preferences), but it stays as a cheap backstop: without a core currency there's
        // nothing to normalise against.
        const preference = this.userStoreService.preferences.preferenceView();
        if (isStale() || !preference) {
          // Logged apart: a clear is an expected logout race, missing preferences mid-session isn't.
          if (isStale()) {
            this.log.info("Balance alignment finished after a clear — discarding stale result");
          } else {
            this.log.warning("Balance alignment finished with no user preferences — discarding it");
          }
          return Promise.resolve(workerMessage);
        }
        this.generatedTransactions = [];
        this.updatedTransactions = {};

        if (workerMessage?.generatedTransactions) {
          const normalizeTransaction = new NormalizeTransaction(
            this.log,
            preference.coreCurrency,
            referenceData,
          );

          // set the generated transactions
          for (const transactionObj of workerMessage.generatedTransactions) {
            const transaction = getTransactionViewOf(transactionObj);
            normalizeTransaction.normalizeImportedTransaction(transaction);
            this.generatedTransactions.push(transaction);
          }
        }
        if (workerMessage?.updatedTransactions) {
          // set the updated transactions
          this.updatedTransactions = workerMessage.updatedTransactions;
        }
        return Promise.resolve(workerMessage);
      });
  }

  applyBalanceAlignment(
    transactions: TransactionView[],
    referenceData: ReferenceDataView[],
  ): TransactionView[] {
    let updatedTransactions = transactions;

    if (this.updatedTransactions) {
      const preference = this.userStoreService.preferences.preferenceView();
      // Null after userStore.clearStore() (logout): leave the transactions as they are.
      if (preference && Object.keys(this.updatedTransactions).length > 0) {
        const normalizeTransaction = new NormalizeTransaction(
          this.log,
          preference.coreCurrency,
          referenceData,
        );

        updatedTransactions.forEach((transaction) => {
          if (this.updatedTransactions?.[transaction.id]) {
            const amount = this.updatedTransactions[transaction.id];
            if (amount < 0) {
              transaction.direction = TransactionDirection.Out;
            } else {
              transaction.direction = TransactionDirection.In;
            }
            transaction.quantity.actualQuantity.amount = Math.abs(amount);
            // need to enrich allocations again because amount could have changed
            enrichAllocations(transaction);
            normalizeTransaction.normalizeImportedTransaction(transaction);
          }
        });
      }
    }

    if (this.generatedTransactions.length > 0) {
      updatedTransactions = transactions.concat(this.generatedTransactions);
    }
    return updatedTransactions;
  }
}
